import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { OTP_DELAY_MINUTES, OTP_EARLY_MINUTES } from 'state/constants';
import { parseDate } from 'state/helpers';


const getStartTime = (flight, timeMode) =>
    timeMode === 'UTC'
        ? new Date(flight.fullAtd || flight.fullEtd)
        : new Date(flight.fullAtdLocalBase || flight.fullEtdLocalBase);

const getEndTime = (flight, timeMode) =>
    timeMode === 'UTC'
        ? new Date(flight.fullAta || flight.fullEta)
        : new Date(flight.fullAtaLocalBase || flight.fullEtaLocalBase);

const buildTaskText = (flight, cellWidth) => {
    const id = flight._id;
    return cellWidth > 100
        ? `<div class="flight-multi-line-text" data-id="${id}">${flight.dep}<br/>${flight.etd} ${flight.flightNo} ${flight.eta}<br/>${flight.arr}</div>`
        : `<div class="flight-multi-line-text" data-id="${id}">${flight.dep}<br/>${flight.flightNo}<br/>${flight.arr}</div>`;
};

const buildDelayTasks = (flight, timeMode, parentId) => {
    const tasks = [];

    const std = new Date(flight.fullStd);
    const stdLB = new Date(flight.fullStdLocalBase);
    const atd = new Date(flight.fullAtd || flight.fullEtd);
    const atdLB = new Date(flight.fullAtdLocalBase || flight.fullEtdLocalBase);
    const sta = new Date(flight.fullSta);
    const staLB = new Date(flight.fullStaLocalBase);
    const ata = new Date(flight.fullAta || flight.fullEta);
    const ataLB = new Date(flight.fullAtaLocalBase || flight.fullEtaLocalBase);

    const depDelayMin = !isNaN(atd) && !isNaN(std) ? (atd - std) / 60000 : 0;
    const arrDelayMin = !isNaN(ata) && !isNaN(sta) ? (ata - sta) / 60000 : 0;

    // Departure delay
    if (depDelayMin > OTP_DELAY_MINUTES) {
        const start = timeMode === 'UTC' ? std : stdLB;
        const end = timeMode === 'UTC' ? atd : atdLB;
        tasks.push({
            id: `${flight._id}_delay_dep`,
            text: "",
            start_date: format(toZonedTime(start, 'UTC'), 'dd-MM-yyyy HH:mm'),
            duration: Math.max(0, (end - start) / 3600000),
            punctuality: 'DelayDep',
            parent: parentId,
        });
    }

    // Arrival delay (only when dep on-time)
    if (depDelayMin <= OTP_DELAY_MINUTES && arrDelayMin > OTP_DELAY_MINUTES) {
        const start = timeMode === 'UTC' ? sta : staLB;
        const end = timeMode === 'UTC' ? ata : ataLB;
        tasks.push({
            id: `${flight._id}_delay_arr`,
            text: "",
            start_date: format(toZonedTime(start, 'UTC'), 'dd-MM-yyyy HH:mm'),
            duration: Math.max(0, (end - start) / 3600000),
            punctuality: 'DelayArr',
            parent: parentId,
        });
    }

    // Early arrival
    const earlyMin = !isNaN(ata) && !isNaN(sta) ? (sta - ata) / 60000 : 0;
    if (earlyMin > OTP_EARLY_MINUTES) {
        const start = timeMode === 'UTC' ? ata : ataLB;
        const end = timeMode === 'UTC' ? sta : staLB;
        tasks.push({
            id: `${flight._id}_early_arr`,
            text: "",
            start_date: format(toZonedTime(start, 'UTC'), 'dd-MM-yyyy HH:mm'),
            duration: Math.max(0, (end - start) / 3600000),
            punctuality: 'EarlyArr',
            parent: parentId,
        });
    }

    return tasks;
};


export const processData = (flights, timeMode, cellWidth) => {
    const tasks = [];
    const links = [];

    // Group by aircraft registration, sorted
    const flightsByReg = new Map();
    flights.forEach(f => {
        const reg = f.flightReg?.includes('-') ? f.flightReg.split('-')[1] : f.flightReg;
        const key = `${f.acType} ${reg || 'N/A'}`;
        if (!flightsByReg.has(key)) flightsByReg.set(key, []);
        flightsByReg.get(key).push(f);
    });

    const sortedKeys = [...flightsByReg.keys()].sort();

    for (const key of sortedKeys) {
        const parentId = `parent_${key}`;
        tasks.push({ id: parentId, text: key, isParent: true });

        for (const flight of flightsByReg.get(key)) {
            const startTime = getStartTime(flight, timeMode);
            const endTime = getEndTime(flight, timeMode);
            if (isNaN(startTime) || isNaN(endTime)) continue;

            tasks.push({
                id: flight._id,
                text: buildTaskText(flight, cellWidth),
                start_date: format(toZonedTime(startTime, 'UTC'), 'dd-MM-yyyy HH:mm'),
                duration: Math.max(0, (endTime - startTime) / 3600000),
                progress: flight.progress || 0,
                parent: parentId,
                flightDate: flight.flightDate,
                flightDateLocalBase: flight.flightDateLocalBase,
                flightDesc: flight.flightDesc,
                flightNo: flight.flightNo,
                dep: flight.dep,
                arr: flight.arr,
                sector: flight.sector,
                sectorType: flight.sectorType || 'Unknown',
                etd: flight.etd,
                eta: flight.eta,
                atd: flight.atd,
                ata: flight.ata,
                etdLocalBase: flight.etdLocalBase,
                etaLocalBase: flight.etaLocalBase,
                atdLocalBase: flight.atdLocalBase,
                ataLocalBase: flight.ataLocalBase,
                expectedPaxPerCategory: flight.expectedPaxPerCategory,
                paxPerCategory: flight.paxPerCategory,
                crewRoutes: flight.crewRoutes || [],
            });

            tasks.push(...buildDelayTasks(flight, timeMode, parentId));
        }
    }

    // Build crew route links
    const flightIdSet = new Set(flights.map(f => f._id));
    const routeMap = new Map();

    flights.forEach(flight => {
        flight.crewRoutes?.forEach(route => {
            if (!route.routeNo || !route.routeDate) return;
            const key = `${parseDate(route.routeDate, 'dd/MM/yyyy')}_${route.routeNo}`;
            if (!routeMap.has(key)) routeMap.set(key, []);
            if (flightIdSet.has(flight._id)) routeMap.get(key).push(flight);
        });
    });

    routeMap.forEach(routeFlights => {
        if (routeFlights.length < 2) return;
        const sorted = [...routeFlights].sort(
            (a, b) => new Date(a.fullAtd || a.fullEtd) - new Date(b.fullAtd || b.fullEtd)
        );
        for (let i = 0; i < sorted.length - 1; i++) {
            links.push({
                id: sorted[i]._id,
                source: sorted[i]._id,
                target: sorted[i + 1]._id,
                type: "0",
            });
        }
    });

    return { tasks, links };
};
